import React, { useCallback, useMemo, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { Typography, Tooltip } from '@mui/material';
import { LabelValue, TimesheetLabelValue } from 'graphql/types';
import { SimpleChip, SearchableChip } from 'modules/common';
import { useChipState } from '../../../../modules/common/hooks/useChipState';
import { DependantLabels } from '../schema';
import useBilling from './useBilling';

type BillingChipProps = {
  onSelect: (value: LabelValue | null) => void;
  disabled?: boolean;
  dependantLabels: DependantLabels;
};

const useStyles = makeStyles(() => ({
  simpleChip: {
    maxWidth: '100%',
    minWidth: '50px',
    cursor: 'not-allowed'
  }
}));

const useAutoCompleteStyles = makeStyles((theme) => ({
  listbox: {
    maxHeight: theme.spacing(19)
  }
}));

const getOptionSelected = (
  option: TimesheetLabelValue,
  selected: TimesheetLabelValue
) => option.id === selected?.id;

export const BillingChip: React.FC<BillingChipProps> = ({
  disabled = false,
  dependantLabels,
  onSelect
}: BillingChipProps) => {
  const classes = useStyles();
  const autoCompleteStyles = useAutoCompleteStyles();

  const [billingChipOpen, setBillingChipOpen] = useState(false);

  const { options, handleSearch, hasMore, loading, label } = useBilling(
    dependantLabels,
    billingChipOpen
  );

  const { isEmptyValue: isEmpty } = useChipState(label);

  const openHandler = useCallback(() => {
    handleSearch('');
    setBillingChipOpen(true);
  }, [handleSearch]);

  const onChipDelete = useCallback(() => {
    onSelect(null);
  }, [onSelect]);

  const onClose = useCallback(() => {
    setBillingChipOpen(false);
  }, [setBillingChipOpen]);

  const optionLabel = useCallback(
    (option: TimesheetLabelValue) => option.name || '',
    []
  );

  const onSelectValue = useCallback(
    (value: TimesheetLabelValue | null) => {
      const transformedValue = {
        idValue: value?.id,
        numericValue: null,
        stringValue: value?.name
      };
      onSelect(transformedValue);
    },
    [onSelect]
  );

  const acceptSuggestionHandler = useCallback(
    (value: LabelValue) => onSelect(value),
    [onSelect]
  );
  const defaultRenderOption = useCallback((option: TimesheetLabelValue) => {
    return (
      <Tooltip followCursor={true} title={option.name || ''} disableInteractive>
        <Typography variant="inherit" noWrap>
          {option.name}
        </Typography>
      </Tooltip>
    );
  }, []);
  const handleDelete = useMemo(() => {
    return !isEmpty ? { handleDelete: onChipDelete } : null;
  }, [isEmpty, onChipDelete]);

  const value = useMemo(
    () =>
      label?.value
        ? {
            id: label.value.idValue ?? '',
            name: label.value.stringValue ?? ''
          }
        : undefined,
    [label]
  );

  const chip = useMemo(
    () =>
      disabled ? (
        <SimpleChip
          className={classes.simpleChip}
          label={label}
          key={label?.id}
          disabled={disabled}
        />
      ) : (
        <SearchableChip
          onClose={onClose}
          label={label}
          loading={loading}
          options={options}
          onOpen={openHandler}
          onSelect={onSelectValue}
          getOptionLabel={optionLabel}
          renderOption={defaultRenderOption}
          disabled={disabled}
          {...handleDelete}
          getOptionSelected={getOptionSelected}
          value={value}
          handleSearch={handleSearch}
          hasMore={hasMore}
          acceptSuggestion={acceptSuggestionHandler}
          overrideClasses={autoCompleteStyles}
        />
      ),
    [
      acceptSuggestionHandler,
      autoCompleteStyles,
      classes.simpleChip,
      defaultRenderOption,
      disabled,
      handleDelete,
      handleSearch,
      hasMore,
      label,
      loading,
      onClose,
      onSelectValue,
      openHandler,
      optionLabel,
      options,
      value
    ]
  );

  return <>{chip}</>;
};
